import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/lib/theme";
import { LanguageProvider } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/Home";
import ProductFinderPage from "@/pages/ProductFinder";
import GameserversPage from "@/pages/Gameservers";
import GameserverDetailPage from "@/pages/GameserverDetail";
import VPSPage from "@/pages/VPS";
import VPSDetailPage from "@/pages/VPSDetail";
import DedicatedPage from "@/pages/Dedicated";
import DedicatedStandardPage from "@/pages/DedicatedStandard";
import DedicatedPremiumPage from "@/pages/DedicatedPremium";
import DomainsPage from "@/pages/Domains";
import WebhostingPage from "@/pages/Webhosting";
import WebhostingSSDPage from "@/pages/WebhostingSSD";
import WebhostingNVMePage from "@/pages/WebhostingNVMe";
import WebhostingNextcloudPage from "@/pages/WebhostingNextcloud";
import RootserverPage from "@/pages/Rootserver";
import RootserverDetailPage from "@/pages/RootserverDetail";
import TeamspeakPage from "@/pages/Teamspeak";
import LicensesPage from "@/pages/Licenses";
import VPNPage from "@/pages/VPN";
import SSLPage from "@/pages/SSL";

import { CookieBanner } from "@/components/CookieBanner";
import { ExternalRedirect } from "@/components/ExternalRedirect";
import REDIRECTS from "@/data/redirects.json";
import CookieSettingsPage from "@/pages/CookieSettings";
import ImpressumPage from "@/pages/Impressum";
import AGBPage from "@/pages/AGB";
import DatenschutzPage from "@/pages/Datenschutz";
import NotFoundPage from "@/pages/NotFound";

// Optional preview routes (controlled via .env)
const showWhmcsPreview  = process.env.REACT_APP_SHOW_WHMCS_PREVIEW  === 'true';
const showDesignSystem  = process.env.REACT_APP_SHOW_DESIGN_SYSTEM  === 'true';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const WhmcsVpsPreview      = showWhmcsPreview  ? require("@/pages/WhmcsVpsPreview").default       : null;
const WhmcsClientDashboard = showWhmcsPreview  ? require("@/pages/WhmcsClientDashboard").default  : null;
const WhmcsGuidePage       = showWhmcsPreview  ? require("@/pages/WhmcsGuide").default             : null;
const DesignSystemPage     = showDesignSystem  ? require("@/pages/DesignSystem").default           : null;

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <ThemeProvider defaultTheme="dark">
        <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Main Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/product-finder" element={<ProductFinderPage />} />
          
          {/* Domains */}
          <Route path="/domains" element={<DomainsPage />} />
          <Route path="/domains/check" element={<DomainsPage />} />
          <Route path="/domains/register" element={<DomainsPage />} />
          <Route path="/domains/transfer" element={<DomainsPage />} />
          
          {/* Web Hosting */}
          <Route path="/webhosting" element={<WebhostingPage />} />
          <Route path="/webhosting/ssd" element={<WebhostingSSDPage />} />
          <Route path="/webhosting/nvme" element={<WebhostingNVMePage />} />
          <Route path="/webhosting/nextcloud" element={<WebhostingNextcloudPage />} />
          
          {/* VPS */}
          <Route path="/vps" element={<VPSPage />} />
          <Route path="/vps/:slug" element={<VPSDetailPage />} />
          
          {/* Rootserver */}
          <Route path="/rootserver" element={<RootserverPage />} />
          <Route path="/rootserver/:slug" element={<RootserverDetailPage />} />
          
          {/* Dedicated Servers */}
          <Route path="/dedicated" element={<DedicatedPage />} />
          <Route path="/dedicated/standard" element={<DedicatedStandardPage />} />
          <Route path="/dedicated/premium" element={<DedicatedPremiumPage />} />
          
          {/* Voice & Gaming */}
          <Route path="/teamspeak" element={<TeamspeakPage />} />
          <Route path="/gameservers" element={<GameserversPage />} />
          <Route path="/gameservers/:slug" element={<GameserverDetailPage />} />
          
          {/* Other Services */}
          <Route path="/licenses" element={<LicensesPage />} />
          <Route path="/vpn" element={<VPNPage />} />
          <Route path="/ssl" element={<SSLPage />} />
          
          {/* Partner Page */}
          <Route path="/partners" element={<HomePage />} />
          
          {/* Legal pages */}
          <Route path="/about" element={<HomePage />} />
          <Route path="/team" element={<HomePage />} />
          <Route path="/careers" element={<HomePage />} />
          <Route path="/imprint" element={<ImpressumPage />} />
          <Route path="/privacy" element={<DatenschutzPage />} />
          <Route path="/terms" element={<AGBPage />} />
          <Route path="/cookies" element={<CookieSettingsPage />} />
          <Route path="/cookie-settings" element={<CookieSettingsPage />} />
          <Route path="/faq" element={<HomePage />} />

          {/* Optional: Design System & WHMCS Preview (configurable via .env) */}
          {showDesignSystem  && DesignSystemPage      && <Route path="/design-system"      element={<DesignSystemPage />} />}
          {showWhmcsPreview  && WhmcsVpsPreview       && <Route path="/whmcs-preview"       element={<WhmcsVpsPreview />} />}
          {showWhmcsPreview  && WhmcsClientDashboard  && <Route path="/whmcs-dashboard"     element={<WhmcsClientDashboard />} />}
          {showWhmcsPreview  && WhmcsGuidePage        && <Route path="/whmcs-guide"         element={<WhmcsGuidePage />} />}
          
          {/* Pages that moved to another site (e.g. sale servers → deinserverhost.de) */}
          {Object.entries(REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<ExternalRedirect to={to} />} />
          ))}

          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <CookieBanner />
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </ThemeProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
